import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Sparkles, PartyPopper } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const EASTER_EGGS = {
  matrix: {
    title: 'The Matrix! 🐰',
    message: 'You found the Matrix easter egg!',
    icon: '💊'
  },
  konami: {
    title: 'Konami Code! 🎮',
    message: 'Up Up Down Down Left Right Left Right B A',
    icon: '👾'
  },
  bsod: {
    title: 'Blue Screen! 💀',
    message: 'You crashed the system... on purpose!',
    icon: '🖥️'
  },
  minesweeper: {
    title: 'Minesweeper Pro! 💣',
    message: 'Retro gaming at its finest!',
    icon: '🎯'
  },
  screensaver: {
    title: 'Screen Saver! ✨',
    message: 'The bouncing logo appears!',
    icon: '🌟'
  },
  rightclick: {
    title: 'Context Menu! 📋',
    message: 'You discovered the right-click menu!',
    icon: '🖱️'
  },
  clippy: {
    title: 'Clippy! 📎',
    message: 'Our helpful assistant from 1997!',
    icon: '💛'
  }
};

export default function EasterEggNotification({ eggId, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const egg = EASTER_EGGS[eggId];

  useEffect(() => {
    if (eggId && egg) {
      // Check if already discovered
      const discovered = JSON.parse(localStorage.getItem('discoveredEggs') || '[]');
      if (!discovered.includes(eggId)) {
        // New discovery!
        discovered.push(eggId);
        localStorage.setItem('discoveredEggs', JSON.stringify(discovered));
        setIsVisible(true);
        playSound(SOUNDS.notify, 0.3);

        // Auto-hide after 4 seconds
        const timer = setTimeout(() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [eggId, egg, onClose]);

  if (!egg || !isVisible) return null;

  const discoveredCount = JSON.parse(localStorage.getItem('discoveredEggs') || '[]').length;
  const totalEggs = Object.keys(EASTER_EGGS).length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50, x: '-50%' }}
        animate={{ opacity: 1, y: 0, x: '-50%' }}
        exit={{ opacity: 0, y: -50, x: '-50%' }}
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          zIndex: 10001,
          pointerEvents: 'none'
        }}
      >
        <motion.div
          animate={{ 
            boxShadow: [
              '0 0 0 rgba(255,215,0,0)',
              '0 0 30px rgba(255,215,0,0.5)',
              '0 0 0 rgba(255,215,0,0)'
            ]
          }}
          transition={{ duration: 1.5, repeat: 2 }}
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '3px solid #ffd700',
            borderRadius: '12px',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}
        >
          {/* Trophy Icon */}
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 0.5 }}
            style={{
              width: '50px',
              height: '50px',
              background: 'linear-gradient(135deg, #ffd700 0%, #ffb700 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              boxShadow: '0 4px 12px rgba(255,215,0,0.4)'
            }}
          >
            {egg.icon}
          </motion.div>

          {/* Content */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px'
            }}>
              <Trophy size={16} color="#ffd700" />
              <span style={{
                color: '#ffd700',
                fontSize: '10px',
                fontWeight: 'bold',
                fontFamily: "'Segoe UI', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Easter Egg Found!
              </span>
            </div>
            <h3 style={{
              margin: '0 0 4px 0',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {egg.title}
            </h3>
            <p style={{
              margin: 0,
              color: 'rgba(255,255,255,0.7)',
              fontSize: '12px',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {egg.message}
            </p>
          </div>

          {/* Progress */}
          <div style={{
            textAlign: 'center',
            borderLeft: '1px solid rgba(255,255,255,0.2)',
            paddingLeft: '16px',
            marginLeft: '8px'
          }}>
            <div style={{
              color: '#ffd700',
              fontSize: '20px',
              fontWeight: 'bold',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {discoveredCount}/{totalEggs}
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '9px',
              fontFamily: "'Segoe UI', sans-serif",
              textTransform: 'uppercase'
            }}>
              Found
            </div>
          </div>
        </motion.div>

        {/* Confetti particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ 
              opacity: 1, 
              x: 0, 
              y: 0,
              scale: 1
            }}
            animate={{ 
              opacity: 0,
              x: (Math.random() - 0.5) * 200,
              y: Math.random() * 100 + 50,
              scale: 0,
              rotate: Math.random() * 360
            }}
            transition={{ duration: 1, delay: i * 0.05 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '10px',
              height: '10px',
              background: ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dfe6e9', '#fd79a8'][i],
              borderRadius: i % 2 === 0 ? '50%' : '2px'
            }}
          />
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to trigger easter egg notifications
export function useEasterEgg() {
  const [currentEgg, setCurrentEgg] = useState(null);

  const triggerEgg = (eggId) => {
    setCurrentEgg(eggId);
  };

  const clearEgg = () => {
    setCurrentEgg(null);
  };

  return { currentEgg, triggerEgg, clearEgg };
}
